/* src/workers/protocol.ts — message contracts shared by the main thread and both workers.
 *
 * Types only, no runtime imports, so this compiles under both the DOM program and the
 * WebWorker program (which disagree about the `self` global — PLAN.md Part 2 §3).
 *
 * Everything that crosses a thread boundary here is either a typed array (transferred,
 * zero-copy) or a small plain object (structured-cloned). Geometry never crosses as anything
 * else: structured-cloning megabytes of vertex data on every tile load during a fast pan is
 * exactly the main-thread stall this architecture exists to avoid.
 */

/** A decoded MVT layer, flattened into transferable arrays. */
export interface PackedLayer {
  /** Flat `[x0, y0, x1, y1, …]` in tile-local units (0..extent, y down from the NW corner). */
  coords: Float32Array;
  /** Index into `coords / 2` where each ring begins. */
  ringStarts: Uint32Array;
  /** Vertex count per ring. */
  ringCounts: Uint32Array;
  /** Index into `ringStarts` where each feature's rings begin. */
  featureRingStarts: Uint32Array;
  /** Ring count per feature. */
  featureRingCounts: Uint32Array;
  /** MVT geometry type per feature: 1 point, 2 linestring, 3 polygon. */
  featureTypes: Uint8Array;
  /** Per-feature tag bag. Small, so structured-clone is fine. */
  props: Array<Record<string, string | number | boolean>>;
  extent: number;
}

/**
 * Number of distinct facade window styles in the facade texture array. Shared here — a
 * types/constants module with no side effects — so the worker that assigns a style index and
 * the renderer that builds the texture array can never disagree on the count.
 */
export const FACADE_STYLE_COUNT = 4;

export interface DecodeRequest {
  readonly kind: 'decode';
  readonly id: string;
  readonly z: number;
  readonly x: number;
  readonly y: number;
  readonly url: string;
}

export interface DecodeResult {
  readonly kind: 'decoded';
  readonly id: string;
  readonly z: number;
  readonly x: number;
  readonly y: number;
  readonly layers: Record<string, PackedLayer>;
  readonly bytes: number;
}

export interface DecodeError {
  readonly kind: 'decode-error';
  readonly id: string;
  readonly message: string;
  /** HTTP status when the failure was a response, 0 for network/parse failures. */
  readonly status: number;
}

export interface BuildRequest {
  readonly kind: 'build';
  readonly id: string;
  readonly z: number;
  readonly x: number;
  readonly y: number;
  readonly layers: Record<string, PackedLayer>;
  /** Mercator metres → true ground metres, i.e. cos(latitude). */
  readonly metreScale: number;
}

/** One merged, ready-to-upload mesh for a single (tile, layer) pair. */
export interface PackedMesh {
  readonly layer: string;
  positions: Float32Array;
  /** Per-vertex normal, quantised to int8 (−127…127 ≡ −1…1). Halves upload bytes; a Lambert
   * shade cannot tell the difference and the upload stall during a city fill can. */
  normals: Int8Array;
  /** Per-vertex RGB, quantised to uint8 (0…255 ≡ 0…1), same rationale. */
  colors: Uint8Array;
  /**
   * Per-vertex UV. On walls, `u` runs along the facade and `v` up it, both in units of one
   * storey — which is what lets a single tiled window texture give every building windows at
   * the right scale without a byte of extra geometry.
   */
  uvs: Float32Array;
  indices: Uint32Array;
  /** Per-vertex source feature id, for GPU id-colour picking. */
  featureIds: Uint32Array;
  /**
   * Per-vertex facade-texture layer index, walls only. Selects which style in the facade
   * texture array a building draws with, so several window styles share one draw call.
   * Absent on every other layer.
   */
  texLayer?: Float32Array;
}

/**
 * Road centrelines, kept alongside the ribbon meshes for the ambient traffic sim: cars follow
 * these, pedestrians walk their sidewalk offsets. Same tile-local frame as the meshes.
 */
export interface PackedPaths {
  /** Flat `[x0, z0, x1, z1, …]` in tile-local metres. */
  points: Float32Array;
  /** Four floats per path: `[firstPointIndex, pointCount, classCode, halfWidthM]`. */
  meta: Float32Array;
}

export interface BuildResult {
  readonly kind: 'built';
  readonly id: string;
  readonly z: number;
  readonly x: number;
  readonly y: number;
  readonly meshes: PackedMesh[];
  readonly triangles: number;
  readonly paths?: PackedPaths;
}

export interface BuildError {
  readonly kind: 'build-error';
  readonly id: string;
  readonly message: string;
}

export type DecodeWorkerOut = DecodeResult | DecodeError;
export type GeometryWorkerOut = BuildResult | BuildError;

/** Every buffer in a packed layer, for a `postMessage` transfer list. */
export function layerTransferables(layers: Record<string, PackedLayer>): ArrayBuffer[] {
  const out: ArrayBuffer[] = [];
  for (const layer of Object.values(layers)) {
    out.push(
      layer.coords.buffer as ArrayBuffer,
      layer.ringStarts.buffer as ArrayBuffer,
      layer.ringCounts.buffer as ArrayBuffer,
      layer.featureRingStarts.buffer as ArrayBuffer,
      layer.featureRingCounts.buffer as ArrayBuffer,
      layer.featureTypes.buffer as ArrayBuffer,
    );
  }
  return out;
}

export function meshTransferables(meshes: PackedMesh[]): ArrayBuffer[] {
  const out: ArrayBuffer[] = [];
  for (const m of meshes) {
    out.push(
      m.positions.buffer as ArrayBuffer,
      m.normals.buffer as ArrayBuffer,
      m.colors.buffer as ArrayBuffer,
      m.uvs.buffer as ArrayBuffer,
      m.indices.buffer as ArrayBuffer,
      m.featureIds.buffer as ArrayBuffer,
    );
    if (m.texLayer) out.push(m.texLayer.buffer as ArrayBuffer);
  }
  return out;
}

/* ── Context worker (the country + the globe) ─────────────────────────────────────────── */

/** A named point for labels: country, state, or town. */
export interface LabelPoint {
  readonly n: string;
  readonly lon: number;
  readonly lat: number;
  /** Natural Earth scalerank where present — lower is more important. */
  readonly r?: number;
}

/** Build the flat-world country context (coastline, highways, urban patches, borders). */
export interface ContextBuildRequest {
  readonly kind: 'context-build';
  readonly id: string;
  readonly originMercX: number;
  readonly originMercY: number;
  readonly metreScale: number;
}

export interface ContextBuilt {
  readonly kind: 'context-built';
  readonly id: string;
  readonly meshes: PackedMesh[];
  readonly places: LabelPoint[];
  readonly triangles: number;
}

/** Rasterise the globe's equirectangular texture off the main thread. */
export interface GlobeTextureRequest {
  readonly kind: 'globe-texture';
  readonly id: string;
  readonly width: number;
  readonly height: number;
}

export interface GlobeTextureDone {
  readonly kind: 'globe-texture-done';
  readonly id: string;
  readonly bitmap: ImageBitmap;
  readonly countries: LabelPoint[];
  readonly states: LabelPoint[];
}

export interface ContextError {
  readonly kind: 'context-error';
  readonly id: string;
  readonly message: string;
}

export type ContextWorkerIn = ContextBuildRequest | GlobeTextureRequest;
export type ContextWorkerOut = ContextBuilt | GlobeTextureDone | ContextError;
